// ProductCardComponent.tsx
import React from "react";
import "./ProductCardComponentStyles.css"
import ReactModal from "react-modal";
import ProductCardAdditionalOptions from "./ProductCardAdditionalOptions";
import { useTheme } from "../../hooks/ThemeContext";
import { getRemoteEnv } from '../../config/remoteEnv';
import { useConfig } from "../../hooks/ConfigProvider";
import { useRestaurant } from "../../hooks/RestaurantContext";

type Props = {
    productId: string,
    productData: any,
    productName: string,
    productPrice: number | null, // Update type to allow null
    currencySymbol: string,
    product?: any,
    menus: string[],
    activeMenu: string,
    productDescription?: string,
    productThumbnailUrl?: string,
    additionalOptions?: any[],
    userChoices?: any[],
    addToCart: any,
    menuSettings: any[],
    openingHours: any
}

const ProductCardComponent = (props: Props) => {

    const { theme } = useTheme();
    const { menuOnly } = getRemoteEnv();
    
    const { config } = useConfig();

    const { restaurantId } = useRestaurant();
    const restaurant = config?.restaurants?.[restaurantId];

    // Effective menu-only state, used both for the modal prop and the
    // desktop fit-to-content height below (kept in one place so they stay in sync).
    const isMenuOnly =
        (menuOnly || !restaurant?.general.onlineOrderingEnabled) &&
        !window.location.hostname.includes("demo.");

    // Desktop-only: when in menu-only mode and the product has no selectable
    // options/addons, let the modal shrink to fit its content instead of 90dvh.
    const hasOptions =
        (props.additionalOptions?.length ?? 0) > 0 ||
        (props.userChoices?.length ?? 0) > 0;
    const fitContentHeight = isMenuOnly && !hasOptions;

    const modalCustomStyles = {
        content: {
          top: '50%',
          left: '50%',
          right: 'auto',
          bottom: 'auto',
          marginRight: '-50%',
          transform: 'translate(-50%, -50%)',
          color: theme.cartTextColor,
          padding: '0',
          zIndex: 800000000,
          border: "1px solid "+ theme.borderColor,
          background: "none"
        },
        overlay: {
          backgroundColor: 'rgba(0, 0, 0, 0.5)',
          zIndex: 700000000,
        }
      };
      
      const modalMediaQueryStyles = `
        @keyframes pcc-content-fade {
          from { opacity: 0; }
          to   { opacity: 1; }
        }
        @keyframes pcc-overlay-in {
          from { opacity: 0; }
          to   { opacity: 1; }
        }
        .ReactModal__Overlay {
          animation: pcc-overlay-in 0.22s ease both;
          backdrop-filter: blur(2px);
          -webkit-backdrop-filter: blur(2px);
        }
        /* opacity-only fade is safe regardless of the layout transform below */
        .ReactModal__Content {
          animation: pcc-content-fade 0.3s ease both;
        }

        @media (min-width: 768px) {
          @keyframes pcc-content-pop {
            from { opacity: 0; transform: translate(-50%, -50%) scale(0.96); }
            to   { opacity: 1; transform: translate(-50%, -50%) scale(1); }
          }
          .ReactModal__Content {
            animation: pcc-content-pop 0.34s cubic-bezier(0.22, 1, 0.36, 1) both;
            height: ${fitContentHeight ? 'auto' : '90dvh'};
            ${fitContentHeight ? 'max-height: 90dvh;' : ''}
            max-width: 600px;
            width: 100%;
          }
        }

        @media (prefers-reduced-motion: reduce) {
          .ReactModal__Overlay,
          .ReactModal__Content {
            animation: none !important;
          }
        }
        
        @media (max-width: 767px) {
          .ReactModal__Content {
            ${fitContentHeight ? `
            height: auto !important;
            max-height: 90dvh !important;
            width: calc(100% - 32px) !important;
            max-width: 600px !important;
            top: 50% !important;
            left: 50% !important;
            right: auto !important;
            bottom: auto !important;
            transform: translate(-50%, -50%) !important;
            margin: 0 !important;
            padding: 0 !important;
            ` : `
            height: 100dvh !important;
            width: 100% !important;
            max-width: 100% !important;
            max-height: 100dvh !important;
            top: 0 !important;
            left: 0 !important;
            right: 0 !important;
            bottom: 0 !important;
            transform: none !important;
            margin: 0 !important;
            border-radius: 0 !important;
            padding: env(safe-area-inset-top) env(safe-area-inset-right) env(safe-area-inset-bottom) env(safe-area-inset-left) !important;
            `}
          }
          
          .ReactModal__Overlay {
            padding: 0 !important;
          }
        }
      `;

    const [showModal, setShowModal] = React.useState(false);
    
    // Check if price is valid
    const productActive = !props.product.outOfStock && props.productPrice !== null && props.productPrice !== undefined && props.productPrice > 0;


    const hasThumb = !!props.productThumbnailUrl;
    const showPrice =
        props.productPrice !== null &&
        props.productPrice !== undefined &&
        props.productPrice > 0;

    return (
        <>
          <div
              className={`product-card-component-container ${hasThumb ? "has-thumb" : "no-thumb"} ${productActive ? "" : "is-inactive"}`}
              style={{
                  color: theme.textColor,
                  cursor: productActive ? "pointer" : "default",
                  // theme-driven CSS variables consumed by the stylesheet
                  // (divider, hover accent tint, focus ring, dotted leader, thumb frame)
                  ["--pcc-border" as any]: theme.borderColor,
                  ["--pcc-text" as any]: theme.textColor,
                  ["--pcc-accent" as any]: theme.categoryIndicatorColor || theme.borderColor,
              }}
              role="button"
              tabIndex={productActive ? 0 : -1}
              aria-disabled={!productActive}
              onClick={productActive ? () => { setShowModal(true) } : undefined}
              onKeyDown={productActive ? (e) => {
                  if (e.key === "Enter" || e.key === " ") { e.preventDefault(); setShowModal(true); }
              } : undefined}
          >
            {/* thumb LEFT · text column · price/+ rail RIGHT — compact row */}
            {hasThumb && (
                <div className="pcc-thumb">
                    <img src={props.productThumbnailUrl} alt={props.productName} loading="lazy" decoding="async" />
                </div>
            )}

            <div className="pcc-main">
                <h3 className="pcc-name">{props.productName}</h3>
                {props.productDescription && (
                    <p className="pcc-description">{props.productDescription}</p>
                )}
            </div>

            <div className="pcc-right">
                {productActive && showPrice ? (
                    <span className="pcc-price">
                        <span className="pcc-price-currency">{props.currencySymbol}</span>
                        {props.productPrice!.toFixed(2)}
                    </span>
                ) : (
                    <span className="pcc-badge">
                        {props.product.outOfStock ? "Sold out" : "Unavailable"}
                    </span>
                )}
                {/* menu-only sites are a shop window — nothing to add */}
                {productActive && !isMenuOnly && <span className="pcc-add" aria-hidden="true">+</span>}
            </div>
          </div>

        {showPrice && (
            <ReactModal isOpen={showModal} style={modalCustomStyles}>
                <style>{modalMediaQueryStyles}</style>
                  <ProductCardAdditionalOptions
                      onClose={() => { setShowModal(false) }}
                      productId={props.productId}
                      productData={props.productData}
                      productName={props.productName}
                      productDescription={props.productDescription}     // ← add
                      productThumbnailUrl={props.productThumbnailUrl}   // ← add
                      productPrice={props.productPrice!}
                      currencySymbol={props.currencySymbol}
                      menus={props.menus}
                      activeMenu={props.activeMenu}
                      additionalOptions={props.additionalOptions}
                      userChoices={props.userChoices}
                      addToCart={props.addToCart}
                      setModalOpen={setShowModal}
                      menuSettings={props.menuSettings}
                      openingHours={props.openingHours}
                      menuOnly={isMenuOnly}
                  />
            </ReactModal>
        )}
        </>
    )

}

export default ProductCardComponent;